import {
    Paragraph,
    TextRun,
    Table,
    TableRow,
    TableCell,
    WidthType,
    BorderStyle,
    AlignmentType,
    VerticalAlign,
    HeightRule,
    TableLayoutType,
} from "docx";
import { fixColor, fitImage, fetchImageWithDimensions, FULL_WIDTH, SCALE, createCommonHeader, createCommonFooter } from "../../common.js";

export const createApprTemp3 = async (apprData, startPage) => {
    const style = apprData.style || {};
    const sections = [];

    const SPACER_HEIGHT = 100;
    const BODY_HEIGHT = 14570;

    const MAX_COL_HEIGHT = BODY_HEIGHT - 200;
    const pointMultiplier = 21;
    const HEADING_EST_HEIGHT = (style.label?.size || 14) * pointMultiplier + 120;
    const LINE_EST_HEIGHT = (style.text?.size || 11) * pointMultiplier;
    const fontSize = style.fontSize || 22;
    const CHARS_PER_LINE = Math.floor(860 / fontSize);

    const projects = apprData.projects || [];
    let itemQueue = [...(apprData.questions || [])];
    let projIndex = 0;
    let pageCount = 0;

    if (!itemQueue.length) return [];

    let currentAbsPage = startPage || 1;

    while (itemQueue.length > 0 && pageCount < 30) {
        pageCount++;
        const leftColChildren = [];
        const rightColChildren = [];

        const createQuestionPart = (q, responseText) => {
            const children = [];
            if (q.heading && !q._isContinuation) {
                children.push(new Paragraph({
                    spacing: { after: 120 },
                    children: [
                        new TextRun({
                            text: q.heading || "",
                            color: fixColor(style.label?.color || "#000000"),
                            size: (style.label?.size || 14) * SCALE,
                            font: style.label?.font,
                            bold: style.label?.bold,
                            italics: style.label?.italic,
                            underline: style.label?.underline,
                        }),
                    ],
                }));
            }
            children.push(new Paragraph({
                spacing: { after: 300 },
                children: [
                    new TextRun({
                        text: responseText || "",
                        color: fixColor(style.text?.color || "#333333"),
                        size: (style.text?.size || 11) * SCALE,
                        font: style.text?.font,
                        bold: style.text?.bold,
                        italics: style.text?.italic,
                        underline: style.text?.underline,
                    }),
                ],
            }));
            return children;
        };

        const fillColumn = (remainingHeight) => {
            const colItems = [];
            let currentHeight = 0;

            while (itemQueue.length > 0) {
                const q = itemQueue[0];
                const headingH = (q._isContinuation) ? 0 : HEADING_EST_HEIGHT;
                const response = q.response || "";
                const responseLines = Math.ceil(response.length / CHARS_PER_LINE) || 1;
                const responseH = (responseLines * LINE_EST_HEIGHT) + 300;
                const totalItemH = headingH + responseH;

                if (currentHeight + totalItemH <= remainingHeight) {
                    colItems.push(...createQuestionPart(q, response));
                    currentHeight += totalItemH;
                    itemQueue.shift();
                } else {
                    const availableForResponse = remainingHeight - currentHeight - headingH - 300;
                    if (availableForResponse > LINE_EST_HEIGHT * 2) {
                        const linesThatFit = Math.floor(availableForResponse / LINE_EST_HEIGHT);
                        const charsThatFit = linesThatFit * CHARS_PER_LINE;

                        let splitIndex = charsThatFit;
                        const lastSpace = response.lastIndexOf(' ', splitIndex);
                        if (lastSpace > charsThatFit * 0.7) splitIndex = lastSpace;

                        const firstPart = response.substring(0, splitIndex).trim();
                        const secondPart = response.substring(splitIndex).trim();

                        if (firstPart) {
                            colItems.push(...createQuestionPart(q, firstPart));
                            currentHeight += headingH + (linesThatFit * LINE_EST_HEIGHT) + 300;

                            itemQueue[0] = {
                                ...q,
                                heading: q.heading,
                                response: secondPart,
                                _isContinuation: true
                            };
                        }
                    }
                    break;
                }
            }
            return { items: colItems, height: currentHeight };
        };

        // Fill Left Column
        const leftRes = fillColumn(MAX_COL_HEIGHT);
        leftColChildren.push(...leftRes.items);

        // Fill Right Column
        const rightRes = fillColumn(MAX_COL_HEIGHT);
        rightColChildren.push(...rightRes.items);

        // Sidebar Projects (Exactly 2 slots per page)
        const SIDEBAR_DXA = Math.floor(FULL_WIDTH * 0.34);
        const SIDEBAR_MARGIN = 150;
        const IMG_BOX_WIDTH = SIDEBAR_DXA - (SIDEBAR_MARGIN * 2);
        const IMG_BOX_HEIGHT = 2800;

        const pageProjects = [];
        for (let i = 0; i < 2; i++) {
            pageProjects.push(projects[projIndex++] || null);
        }

        const sidebarChildren = [];

        const addProjectSlot = async (proj) => {
            let projImgRun = null;
            const photo = proj?.uploadedPhoto || proj?.photoURL;
            if (proj && photo) {
                const imgData = await fetchImageWithDimensions(photo);
                if (imgData) {
                    projImgRun = fitImage(imgData, IMG_BOX_WIDTH, IMG_BOX_HEIGHT, "cover");
                }
            }

            sidebarChildren.push(
                new Table({
                    width: { size: SIDEBAR_DXA, type: WidthType.DXA },
                    borders: {
                        top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
                        left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
                        insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE }
                    },
                    rows: [
                        new TableRow({
                            height: { value: IMG_BOX_HEIGHT, rule: HeightRule.EXACT },
                            children: [
                                new TableCell({ width: { size: SIDEBAR_MARGIN, type: WidthType.DXA }, children: [] }),
                                new TableCell({
                                    width: { size: IMG_BOX_WIDTH, type: WidthType.DXA },
                                    shading: projImgRun ? undefined : { fill: "#FFFFFF" },
                                    verticalAlign: VerticalAlign.CENTER,
                                    children: [
                                        new Paragraph({
                                            alignment: AlignmentType.CENTER,
                                            children: [projImgRun].filter(Boolean)
                                        })
                                    ]
                                }),
                                new TableCell({ width: { size: SIDEBAR_MARGIN, type: WidthType.DXA }, children: [] }),
                            ]
                        })
                    ]
                }),
                new Paragraph({
                    alignment: AlignmentType.CENTER,
                    spacing: { before: 100 },
                    children: [
                        new TextRun({
                            text: proj?.projectName || "N/A",
                            color: fixColor(style.heading?.color || "#FFFFFF"),
                            size: (style.heading?.size || 18) * SCALE,
                            font: style.heading?.font,
                            bold: style.heading?.bold !== false,
                            italics: style.heading?.italic,
                            underline: style.heading?.underline,
                        }),
                    ],
                }),
                new Paragraph({
                    alignment: AlignmentType.CENTER,
                    spacing: { after: 100 },
                    children: [
                        new TextRun({
                            text: `${proj?.city || 'N/A'}, ${proj?.stateProvince || 'N/A'}`,
                            color: fixColor(style.heading?.color || "#FFFFFF"),
                            size: (style.heading?.size || 18) * SCALE,
                            font: style.heading?.font,
                            bold: style.heading?.bold !== false,
                            italics: style.heading?.italic,
                            underline: style.heading?.underline,
                        }),
                    ],
                }),
                new Paragraph({ spacing: { after: 400 }, children: [] })
            );
        };

        for (let p = 0; p < 2; p++) {
            await addProjectSlot(pageProjects[p]);
        }

        sidebarChildren.unshift(new Paragraph({ spacing: { before: 100 }, children: [] }));
        sidebarChildren.push(new Paragraph({ spacing: { after: 100 }, children: [] }));

        const headerTable = createCommonHeader(apprData.title, fixColor(style.colorScheme?.header || "#545454"));
        const footerTable = createCommonFooter(currentAbsPage, fixColor(style.colorScheme?.footer || "#545454"));

        // 2. BODY TABLE (3 Columns)
        const bodyTable = new Table({
            width: { size: FULL_WIDTH, type: WidthType.DXA },
            layout: TableLayoutType.FIXED,
            borders: {
                top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
                left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
                insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE }
            },
            rows: [
                new TableRow({
                    height: { value: BODY_HEIGHT, rule: HeightRule.EXACT },
                    children: [
                        new TableCell({ width: { size: 2, type: WidthType.PERCENTAGE }, children: [] }),
                        new TableCell({
                            width: { size: 30, type: WidthType.PERCENTAGE },
                            children: leftColChildren,
                        }),
                        new TableCell({ width: { size: 2, type: WidthType.PERCENTAGE }, children: [] }),
                        new TableCell({
                            width: { size: 30, type: WidthType.PERCENTAGE },
                            children: rightColChildren,
                        }),
                        new TableCell({ width: { size: 2, type: WidthType.PERCENTAGE }, children: [] }),
                        new TableCell({
                            width: { size: 34, type: WidthType.PERCENTAGE },
                            shading: { fill: fixColor(style.colorScheme?.body || "#999999") },
                            children: sidebarChildren,
                        }),
                    ],
                }),
            ],
        });

        sections.push({
            properties: {
                page: {
                    margin: { top: 0, right: 0, bottom: 0, left: 0 },
                },
            },
            children: [
                headerTable,
                new Paragraph({ spacing: { before: SPACER_HEIGHT, after: 0 }, children: [] }),
                bodyTable,
                new Paragraph({ spacing: { before: SPACER_HEIGHT, after: 0 }, children: [] }),
                footerTable
            ],
        });

        currentAbsPage++;
        if (leftColChildren.length === 0 && rightColChildren.length === 0) break;
    }

    return sections;
};
